import React, { useCallback, useMemo, useRef, useState } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  Dimensions,
  Keyboard,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import {
  BottomSheetModal,
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme } from '../../theme';
import Button from '../Reusables/Button';
import AppSkeleton from '../Reusables/AppSkeleton';
import { borderWidth, fs, iconSize, s, v } from '../../utils/libs';
import {
  getProductDisplayName,
  type ProductLike,
} from '../../utils/backofficeSuppliers';
import { shouldUseExpandedLayout } from '../../utils/layoutUtils';
import { applyExpandedLayoutSnapBoost } from '../../utils/bottomSheetSnapPoints';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import SheetBackGuard from '../Reusables/bottomSheet/SheetBackGuard';

type ProductShape = ProductLike & {
  id?: string;
  productId?: string;
  name?: string;
  slug?: string;
  measurementType?: string;
  quantity?: number;
  unit?: string;
  unitOfMass?: string;
  isWeighted?: boolean;
};

type OptionItem = {
  id: string;
  product: ProductShape;
  label: string;
};

type Props = {
  products: ProductShape[];
  selectedProduct: ProductShape | null;
  onSelectProduct: (product: ProductShape) => void;
  disabled?: boolean;
  loadingProducts?: boolean;
  onAddProduct?: () => void;
};

const WebBackdrop: React.FC<{
  animatedIndex: SharedValue<number>;
  style: any;
  onPress: () => void;
}> = ({ animatedIndex, style, onPress }) => {
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(animatedIndex.value, [-1, 0], [0, 0.5]),
    display: animatedIndex.value < -0.5 ? 'none' : 'flex',
  }));
  return (
    <Animated.View style={[style, { backgroundColor: '#000' }, animatedStyle]}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onPress} />
    </Animated.View>
  );
};

const SupplierProductPicker: React.FC<Props> = ({
  products,
  selectedProduct,
  onSelectProduct,
  disabled,
  loadingProducts = false,
  onAddProduct,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const { width: winWidth, height: winHeight } = useWindowDimensions();
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const options = useMemo(() => {
    return (products || []).map(product => {
      const id = String(product?.productId || product?._id || '');
      const label = getProductDisplayName(product);
      return {
        id,
        product,
        label: label || String(product?.name || 'Unnamed product'),
      };
    });
  }, [products]);

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return options;
    return options.filter(option => option.label.toLowerCase().includes(query));
  }, [options, searchTerm]);

  const selectedLabel = useMemo(() => {
    if (!selectedProduct) return 'Select product';
    const text = getProductDisplayName(selectedProduct);
    return text || String(selectedProduct?.name || 'Select product');
  }, [selectedProduct]);

  const onPick = useCallback(
    (product: ProductShape) => {
      onSelectProduct(product);
      bottomSheetRef.current?.dismiss();
      setSearchTerm('');
    },
    [onSelectProduct],
  );

  const onClose = useCallback(() => {
    bottomSheetRef.current?.dismiss();
    setSearchTerm('');
  }, []);

  const handleAddProduct = useCallback(() => {
    bottomSheetRef.current?.dismiss();
    setSearchTerm('');
    onAddProduct?.();
  }, [onAddProduct]);

  const handleSheetChanges = useCallback((index: number) => {
    setIsSheetOpen(index >= 0);
    if (index === -1) {
      setSearchTerm('');
    }
  }, []);

  const renderBackdrop = useCallback((props: BottomSheetBackdropProps) => {
    if (Platform.OS === 'web') {
      return (
        <WebBackdrop
          animatedIndex={props.animatedIndex}
          style={props.style}
          onPress={() => bottomSheetRef.current?.dismiss()}
        />
      );
    }
    return (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
      />
    );
  }, []);

  const hasNoProducts = options.length === 0 && !loadingProducts;
  const baseSnapPoints = useMemo(
    () => (hasNoProducts ? ['65%', '90%'] : ['50%', '90%']),
    [hasNoProducts],
  );
  const snapPoints = useMemo(() => {
    if (!shouldUseExpandedLayout(winWidth, winHeight)) {
      return baseSnapPoints;
    }
    return applyExpandedLayoutSnapBoost(baseSnapPoints);
  }, [baseSnapPoints, winWidth, winHeight]);

  const renderItem = useCallback(
    ({ item }: { item: OptionItem }) => (
      <TouchableOpacity
        style={styles.optionRow}
        onPress={() => onPick(item.product)}
        activeOpacity={0.6}>
        <Text style={styles.optionText}>{item.label}</Text>
      </TouchableOpacity>
    ),
    [onPick, styles.optionRow, styles.optionText],
  );

  const keyExtractor = useCallback(
    (item: OptionItem) => item.id || item.label,
    [],
  );

  const listContentMinHeight = useMemo(
    () => Dimensions.get('window').height * 0.35,
    [],
  );

  const ListEmptyComponent = useMemo(() => {
    if (loadingProducts) {
      return <AppSkeleton mode="product_picker" rows={12} />;
    }
    if (options.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons
            name="package-variant"
            size={iconSize(64)}
            color={theme.colors.textTertiary}
          />
          <Text style={styles.emptyText}>Product names not found</Text>
          <Text style={styles.emptySubtext}>
            Add products to your inventory to select them here.
          </Text>
          {onAddProduct ? (
            <Button
              backgroundColor={theme.colors.success}
              rippleColor={theme.colors.success}
              text="Add Product"
              fontFamily={GTFonts.regular}
              icon=""
              height={44}
              paddingHorizontal={44}
              borderRadius={theme.borderRadius.md}
              onPress={handleAddProduct}
            />
          ) : null}
        </View>
      );
    }
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No product found for this search.</Text>
        {onAddProduct ? (
          <Button
            backgroundColor={theme.colors.success}
            rippleColor={theme.colors.success}
            text="Add Product"
            fontFamily={GTFonts.regular}
            icon=""
            height={44}
            paddingHorizontal={44}
            borderRadius={theme.borderRadius.md}
            onPress={handleAddProduct}
          />
        ) : null}
      </View>
    );
  }, [
    loadingProducts,
    options.length,
    onAddProduct,
    handleAddProduct,
    theme.borderRadius.md,
    theme.colors.primary,
    theme.colors.textTertiary,
    theme.colors.success,
    styles.emptyContainer,
    styles.loadingContainer,
    styles.emptyText,
    styles.emptySubtext,
    styles.loadingText,
  ]);

  const header = (
    <View style={styles.header}>
      <View style={styles.headerRow}>
        <Text style={styles.modalTitle}>Select Product</Text>
        <TouchableOpacity
          onPress={onClose}
          style={styles.closeButton}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Text style={styles.closeLabel}>Close</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.searchWrapper}>
        <MaterialCommunityIcons
          name="magnify"
          size={iconSize(20)}
          color={theme.colors.textSecondary}
          style={styles.searchIcon}
        />
        <BottomSheetTextInput
          placeholder="Search products by name..."
          placeholderTextColor={theme.colors.textSecondary}
          value={searchTerm}
          onChangeText={setSearchTerm}
          style={[styles.searchInput, { color: theme.colors.text }]}
          autoCapitalize="none"
          autoCorrect={false}
        />
      </View>
    </View>
  );

  return (
    <>
      <TouchableOpacity
        disabled={Boolean(disabled)}
        style={[styles.selector, disabled && styles.selectorDisabled]}
        onPress={() => {
          Keyboard.dismiss();
          bottomSheetRef.current?.present();
        }}>
        <Text
          numberOfLines={1}
          style={[
            styles.selectorText,
            !selectedProduct && styles.placeholderText,
          ]}>
          {selectedLabel}
        </Text>
        <MaterialCommunityIcons
          name="chevron-down"
          color={theme.colors.textSecondary}
          size={iconSize(20)}
        />
      </TouchableOpacity>

      <SheetBackGuard open={isSheetOpen} dismissible dismiss={onClose} />
      <BottomSheetModal
        ref={bottomSheetRef}
        index={0}
        snapPoints={snapPoints}
        bottomInset={insets.bottom}
        // animateOnMount
        enableDynamicSizing={false}
        enablePanDownToClose
        enableOverDrag={true}
        android_keyboardInputMode="adjustPan"
        keyboardBehavior="extend"
        keyboardBlurBehavior="restore"
        backdropComponent={renderBackdrop}
        onChange={handleSheetChanges}>
        <View style={[styles.container, styles.webSheetInner]}>
          {header}
          <BottomSheetFlatList
            data={filteredOptions}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            ListEmptyComponent={ListEmptyComponent}
            contentContainerStyle={[
              styles.listContent,
              { flexGrow: 1, minHeight: listContentMinHeight },
            ]}
            style={styles.list}
          />
        </View>
      </BottomSheetModal>
    </>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    selector: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: borderWidth(1.2),
      borderColor: theme.colors.borderGray || theme.colors.border,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(12),
      height: v(50),
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    selectorDisabled: {
      opacity: 0.6,
    },
    selectorText: {
      flex: 1,
      marginRight: s(8),
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    placeholderText: {
      color: theme.colors.textTertiary,
    },
    container: {
      flex: 1,
      backgroundColor: theme.colors.surface,
    },
    /** Matches CustomBottomSheet `constrainWidth` / webContentWrapper */
    webSheetInner:
      Platform.OS === 'web'
        ? {
            maxWidth: WEB_MAX_CONTENT_WIDTH,
            alignSelf: 'center',
            width: '100%',
            paddingHorizontal: s(24),
          }
        : {},
    header: {
      paddingHorizontal: Platform.OS === 'web' ? 0 : s(14),
      paddingTop: v(4),
      paddingBottom: v(12),
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: v(12),
    },
    modalTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: theme.colors.text,
    },
    closeButton: {
      paddingHorizontal: s(10),
      paddingVertical: v(6),
    },
    closeLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.errorDark,
    },
    searchWrapper: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: borderWidth(1.2),
      borderColor: theme.colors.borderGray || theme.colors.border,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
    },
    searchIcon: {
      marginLeft: s(12),
    },
    searchInput: {
      flex: 1,
      height: v(48),
      paddingHorizontal: s(12),
      paddingVertical: v(12),
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      ...(Platform.OS === 'web' && {
        height: v(52),
      }),
    },
    list: {
      flex: 1,
    },
    listContent: {
      paddingBottom: v(24),
    },
    loadingContainer: {
      justifyContent: 'center',
      gap: v(12),
    },
    loadingText: {
      fontSize: fs(14),
      lineHeight: fs(18),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    optionRow: {
      paddingVertical: v(10),
      paddingHorizontal: Platform.OS === 'web' ? 0 : s(14),
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    optionText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    emptyContainer: {
      flex: 1,
      minHeight: 240,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: v(24),
      gap: v(12),
    },
    emptyText: {
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    emptySubtext: {
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textTertiary,
      marginTop: v(4),
    },
  });

export default SupplierProductPicker;
